/**
 * Calendar View — V5.1 multi-dimensional
 *
 * 4 time fields × 3 layouts = 12 sub-configurations.
 *
 * Time field (the "what" on the timeline):
 *   - Due       — when work is expected (planner, forward)
 *   - Created   — when work was added (history, backward)
 *   - Completed — when work was done (history, backward)
 *   - Date      — the work's own date prop (planner, forward)
 *
 * Layout (the "how" it's visualized):
 *   - Month    — 7×6 calendar grid, traditional (no scroll direction)
 *   - Agenda   — vertical list, day-grouped (no scroll direction)
 *   - Columns  — horizontal kanban-by-date, today anchored left,
 *                scroll right = past (history) or future (planner)
 *
 * Today is ALWAYS anchored on the left. Direction depends on the time field.
 *
 * Recipe: surface.workspace.calendar
 * Behaviours: switch-time-field, switch-layout
 * Module: module.database
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Paper, Text, Group, Badge, Stack, SimpleGrid, Box,
  ActionIcon, Chip, Button, Popover, Modal, Kbd,
} from '@mantine/core';
import {
  IconChevronLeft, IconChevronRight, IconChevronDown, IconCalendar,
  IconHistory, IconCheck, IconClock, IconRefresh,
  IconKeyboard, IconSearch, IconFilter, IconX,
} from '@tabler/icons-react';
import { DatabaseItem, Database } from '../../../types/models/Database';
import {
  loadCalendarConfig, saveCalendarConfig,
  ZOOM_LABELS, ZOOM_ORDER, SMART_DEFAULT_ZOOM, CALENDAR_ZOOM_OVERRIDE_KEY,
  resolveItemDate, fmtDay, parseDay, isSameDay, startOfDay, buildWindow, groupByMonth,
  applyFilters, quickFilterLabel, statusFilterLabel, RELATIVE_DATES,
  isoWeek, sortItems, countActiveFilters, SORT_LABELS,
  discoverProperties,
  type ZoomLevel, type TimeField, type CalendarLayout as Layout,
  type QuickFilter, type StatusFilter, type SortKey, type WeekStart,
  type PropertyFilter,
  type DiscoveredProperty,
} from '../database/calendarConfig';
import type { PropertyDefinition, SelectOption, StatusOption } from '../../types/models/Property';

const TIME_FIELD_LABELS: Record<TimeField, { label: string; icon: React.ReactNode; direction: 'forward' | 'backward' }> = {
  due:       { label: 'Due',       icon: <IconCalendar size={12} />, direction: 'forward' },
  created:   { label: 'Created',   icon: <IconHistory size={12} />,  direction: 'backward' },
  completed: { label: 'Completed', icon: <IconCheck size={12} />,    direction: 'backward' },
  date:      { label: 'Date',      icon: <IconClock size={12} />,     direction: 'forward' },
  updated:   { label: 'Updated',   icon: <IconRefresh size={12} />,  direction: 'backward' },
};

const LAYOUT_LABELS: Record<Layout, string> = {
  month: 'Month',
  agenda: 'Agenda',
  columns: 'Columns',
};

/** Resolve the effective date for an item given the chosen time field. */
function getItemDate(item: DatabaseItem, timeField: TimeField, datePropertyId?: string): Date | null {
  if (timeField === 'created') return new Date(item.createdAt);
  if (timeField === 'completed') {
    // Treat as completed if the status property is "done"-ish, else null
    const status = item.properties.find(p => p.propertyId === 'status')?.value as any;
    const isDone = status && typeof status === 'object' && (
      status.state === 'completed' ||
      status.state === 'archived' ||
      (status.id && /done|complete|finish/i.test(String(status.id)))
    );
    if (!isDone) return null;
    return new Date(item.updatedAt);
  }
  // 'due' or 'date' — use the date property
  const propId = datePropertyId || 'date';
  const prop = item.properties.find(p => p.propertyId === propId);
  if (!prop?.value) return null;
  const d = new Date(prop.value);
  return isNaN(d.getTime()) ? null : d;
}

// F 128: the local `fmtDay` here duplicated the one already imported from
// ./calendarConfig on line 38 (byte-identical bodies). Vite's dev server
// rejected the module with "Duplicate declaration \"fmtDay\"", which made
// `npm run dev` fail to boot and took the whole Playwright E2E suite with
// it. The import is the canonical one; the local copy is gone.
// F 128: the local `isSameDay` here duplicated the one imported from
// ./calendarConfig on line 39 (byte-identical body). Same class of defect as
// the `fmtDay` duplicate removed above it: vite's dev server rejects the
// module with "Duplicate declaration", which stops `npm run dev` booting and
// takes the whole Playwright E2E suite with it. The import is canonical.

/**
 * Drag-and-drop handlers for rescheduling an item by dropping it onto
 * a different day. Works across all calendar layouts (month, columns,
 * agenda). The actual mutation goes through onItemUpdate so the
 * underlying data layer (localStorage, db adapter) stays in charge.
 */
const DRAG_TYPE = 'application/x-freshcards-item';
function makeItemDragHandlers(
  item: DatabaseItem,
  onItemUpdate?: (item: DatabaseItem) => void,
  datePropertyId?: string
) {
  return {
    draggable: true as const,
    onDragStart: (e: React.DragEvent) => {
      e.dataTransfer.setData(DRAG_TYPE, item.id);
      e.dataTransfer.effectAllowed = 'move';
    },
    onDragEnd: (e: React.DragEvent) => {
      e.dataTransfer.clearData(DRAG_TYPE);
    },
    style: { cursor: 'grab' } as React.CSSProperties,
  };
}
function makeDayDropHandlers(
  targetDate: Date,
  items: DatabaseItem[],
  onItemUpdate?: (item: DatabaseItem) => void,
  datePropertyId: string = 'date'
) {
  return {
    onDragOver: (e: React.DragEvent) => {
      if (e.dataTransfer.types.includes(DRAG_TYPE)) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }
    },
    onDragLeave: (e: React.DragEvent) => {
      // no-op; CSS handles the visual hint
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      const itemId = e.dataTransfer.getData(DRAG_TYPE);
      const item = items.find(i => i.id === itemId);
      if (!item || !onItemUpdate) return;
      // Update the date property on the item
      const newDateStr = fmtDay(targetDate);
      const newProps = item.properties.map(p =>
        p.propertyId === datePropertyId ? { ...p, value: newDateStr } : p
      );
      // If the property doesn't exist, add it
      if (!newProps.some(p => p.propertyId === datePropertyId)) {
        newProps.push({ propertyId: datePropertyId, value: newDateStr });
      }
      onItemUpdate({ ...item, properties: newProps });
    },
  };
}

interface CalendarViewProps {
  database: Database;
  items: DatabaseItem[];
  datePropertyId?: string;
  onItemSelect?: (item: DatabaseItem) => void;
  onItemCreate?: (item: DatabaseItem) => void;
  onItemUpdate?: (item: DatabaseItem) => void;
  onItemDelete?: (itemId: string) => void;
}

// F 85 — G-1: prop type now exported as part of the public view API.
export type { CalendarViewProps };

export const CalendarView: React.FC<CalendarViewProps> = ({
  database,
  items,
  datePropertyId,
  onItemSelect,
  onItemCreate,
  onItemUpdate,
  onItemDelete,
}) => {
  // Single config object — persisted in localStorage as a single JSON blob
  const [config, setConfig] = useState(() => loadCalendarConfig(database.id));
  const [cursor, setCursor] = useState(new Date());
  const [quickJumpOpen, setQuickJumpOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const activeFilterCount = countActiveFilters(config);

  // Global keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't fire shortcuts while user is typing in a text input
      const target = e.target as HTMLElement | null;
      const isTyping = target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      );
      if (isTyping) return;
      // T — jump to today
      if (e.key === 't' || e.key === 'T') {
        e.preventDefault();
        setCursor(new Date());
        return;
      }
      // ← / → — prev/next zoom unit
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCursor(c => { const n = new Date(c); n.setDate(n.getDate() - zoomStepDaysLocal(config.zoom)); return n; });
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCursor(c => { const n = new Date(c); n.setDate(n.getDate() + zoomStepDaysLocal(config.zoom)); return n; });
        return;
      }
      // 1-6 — switch zoom
      const zoomKeys: Record<string, ZoomLevel> = { '1': 'day', '2': '3-day', '3': 'week', '4': 'month', '5': 'quarter', '6': 'year' };
      if (zoomKeys[e.key]) {
        e.preventDefault();
        setConfig(c => ({ ...c, zoom: zoomKeys[e.key] }));
        return;
      }
      // D / C / A / M — switch layout (Day/Columns/Agenda/Month)
      if (e.key === 'd' || e.key === 'D') { e.preventDefault(); setConfig(c => ({ ...c, layout: 'columns' })); return; }
      if (e.key === 'c' || e.key === 'C') { e.preventDefault(); setConfig(c => ({ ...c, layout: 'columns' })); return; }
      if (e.key === 'a' || e.key === 'A') { e.preventDefault(); setConfig(c => ({ ...c, layout: 'agenda' })); return; }
      if (e.key === 'm' || e.key === 'M') { e.preventDefault(); setConfig(c => ({ ...c, layout: 'month' })); return; }
      // Cmd/Ctrl+K — quick-jump
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setQuickJumpOpen(true);
        return;
      }
      // ? — help
      if (e.key === '?') {
        e.preventDefault();
        setHelpOpen(true);
        return;
      }
      // Esc — close any open dialog
      if (e.key === 'Escape') {
        setQuickJumpOpen(false);
        setFiltersOpen(false);
        setHelpOpen(false);
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [config.zoom]);

  useEffect(() => {
    saveCalendarConfig(database.id, config);
  }, [config, database.id]);

  const timeField = config.timeField;
  const layout = config.layout;
  const zoom = config.zoom;
  const setTimeField = (tf: TimeField) => setConfig(c => {
    // When time field changes, consider smart default for zoom if no override
    const overrideKey = CALENDAR_ZOOM_OVERRIDE_KEY(database.id, tf);
    const hasOverride = typeof window !== 'undefined' && localStorage.getItem(overrideKey) != null;
    return {
      ...c,
      timeField: tf,
      zoom: hasOverride ? c.zoom : SMART_DEFAULT_ZOOM[tf],
    };
  });
  const setLayout = (l: Layout) => setConfig(c => ({ ...c, layout: l }));
  const setZoom = (z: ZoomLevel) => {
    setConfig(c => ({ ...c, zoom: z }));
    // Record that the user picked a manual zoom for this timeField — prevents
    // the smart default from kicking back in on next timeField change.
    try { localStorage.setItem(CALENDAR_ZOOM_OVERRIDE_KEY(database.id, timeField), '1'); } catch {}
  };

  // Build the per-day map (after applying filters + sort)
  const filteredItems = useMemo(
    () => sortItems(
      applyFilters(items, timeField, config.quickFilter, config.statusFilter, datePropertyId, config.propertyFilter),
      config.sort, timeField, datePropertyId,
    ),
    [items, timeField, config.quickFilter, config.statusFilter, config.sort, config.propertyFilter, datePropertyId],
  );

  // Universal property discovery — schema-declared + item-observed
  const allProperties: DiscoveredProperty[] = useMemo(
    () => discoverProperties(database?.schema?.properties, items),
    [database?.schema?.properties, items],
  );
  const itemsByDate = useMemo(() => {
    const map = new Map<string, DatabaseItem[]>();
    for (const item of filteredItems) {
      const d = resolveItemDate(item, timeField, datePropertyId);
      if (!d) continue;
      const key = fmtDay(d);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return map;
  }, [filteredItems, timeField, datePropertyId]);

  const direction = TIME_FIELD_LABELS[timeField].direction;

  return (
    <Box className="fc-calendar-root" style={{ width: '100%', padding: '12px 16px' }}>
      {/* COMPACT TOOLBAR (world-class, 2 rows max):
          Row 1: By (time field) + As (layout) — visible always
          Row 2: Quick-jump + Filters (with badge) + Sort + Help
          Active filter chips appear below as a dismissable row */}
      <Group gap="sm" align="center" wrap="wrap" mb="xs">
        <Text size="xs" fw={500} c="dimmed" tt="uppercase" mr={4}>By</Text>
        <Chip.Group
          multiple={false}
          value={timeField}
          onChange={(v) => v && setTimeField(v as TimeField)}
        >
          <Group gap={4} wrap="wrap">
            {(Object.keys(TIME_FIELD_LABELS) as TimeField[]).map(tf => (
              <Chip
                key={tf}
                value={tf}
                size="xs"
                variant={timeField === tf ? 'filled' : 'light'}
                color="blue"
                icon={TIME_FIELD_LABELS[tf].icon}
              >
                {TIME_FIELD_LABELS[tf].label}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
        <Text size="xs" fw={500} c="dimmed" tt="uppercase" ml="md" mr={4}>As</Text>
        <Chip.Group
          multiple={false}
          value={layout}
          onChange={(v) => v && setLayout(v as Layout)}
        >
          <Group gap={4} wrap="wrap">
            {(Object.keys(LAYOUT_LABELS) as Layout[]).map(l => (
              <Chip
                key={l}
                value={l}
                size="xs"
                variant={layout === l ? 'filled' : 'light'}
                color="grape"
              >
                {LAYOUT_LABELS[l]}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
      </Group>

      <Group gap="xs" align="center" wrap="wrap" mb="xs">
        <Popover opened={quickJumpOpen} onChange={setQuickJumpOpen} position="bottom-start" withArrow shadow="md">
          <Popover.Target>
            <Button size="xs" variant="light" color="indigo" leftSection={<IconSearch size={12} />}
                    onClick={() => setQuickJumpOpen(o => !o)}>
              Jump to…
            </Button>
          </Popover.Target>
          <Popover.Dropdown>
            <Stack gap="xs">
              <Text size="xs" c="dimmed" tt="uppercase" fw={500}>Quick jump</Text>
              <Group gap="xs" wrap="wrap">
                {RELATIVE_DATES.map(r => (
                  <Button key={r.label} size="xs" variant="light" color="indigo"
                          onClick={() => { setCursor(r.get()); setQuickJumpOpen(false); }}>
                    {r.label}
                  </Button>
                ))}
              </Group>
              <Text size="xs" c="dimmed">Pick a date:</Text>
              <input type="date" className="fc-jump-date-input" style={{ width: '100%' }}
                     value={fmtDay(cursor)}
                     onChange={e => {
                       const v = e.target.value;
                       if (v) {
                         const [y, m, d] = v.split('-').map(Number);
                         setCursor(new Date(y, m - 1, d));
                         setQuickJumpOpen(false);
                       }
                     }} />
            </Stack>
          </Popover.Dropdown>
        </Popover>

        {/* FILTERS POPOVER — the world-class pattern */}
        <Popover opened={filtersOpen} onChange={setFiltersOpen} position="bottom-start" withArrow shadow="md" width={360}>
          <Popover.Target>
            <Button size="xs" variant="light" color="blue"
                    leftSection={<IconFilter size={12} />}
                    rightSection={activeFilterCount > 0 ? <Badge size="xs" circle color="blue">{activeFilterCount}</Badge> : null}
                    onClick={() => setFiltersOpen(o => !o)}>
              Filters
            </Button>
          </Popover.Target>
          <Popover.Dropdown style={{ maxHeight: 'min(600px, calc(100vh - 100px))', overflowY: 'auto' }}>
            <Stack gap="md">
              {/* Header */}
              <Group justify="space-between" align="center">
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>Filters</Text>
                {activeFilterCount > 0 && (
                  <Button size="xs" variant="subtle" color="gray" onClick={() => {
                    setConfig(c => ({ ...c, quickFilter: 'all', statusFilter: 'all', sort: 'date-asc', weekStart: 'monday', hideWeekends: false, propertyFilter: {} }));
                  }}>
                    Clear all
                  </Button>
                )}
              </Group>

              {/* Zoom */}
              <Stack gap={4}>
                <Text size="xs" c="dimmed" fw={500}>Zoom</Text>
                <Chip.Group
                  multiple={false}
                  value={config.zoom}
                  onChange={(v) => v && setZoom(v as ZoomLevel)}
                >
                  <Group gap={4} wrap="wrap">
                    {ZOOM_ORDER.map(z => (
                      <Chip key={z} value={z} size="xs"
                            variant={config.zoom === z ? 'filled' : 'light'}
                            color="teal">
                        {ZOOM_LABELS[z]}
                      </Chip>
                    ))}
                  </Group>
                </Chip.Group>
              </Stack>

              {/* Where */}
              <Stack gap={4}>
                <Text size="xs" c="dimmed" fw={500}>Where</Text>
                <Chip.Group
                  multiple={false}
                  value={config.quickFilter}
                  onChange={(v) => v && setConfig(c => ({ ...c, quickFilter: v as QuickFilter }))}
                >
                  <Group gap={4} wrap="wrap">
                    {(['all', 'overdue', 'today', 'this-week', 'no-date'] as QuickFilter[]).map(f => (
                      <Chip key={f} value={f} size="xs"
                            variant={config.quickFilter === f ? 'filled' : 'light'}
                            color={f === 'overdue' ? 'red' : f === 'today' ? 'blue' : 'gray'}>
                        {quickFilterLabel(f)}
                      </Chip>
                    ))}
                  </Group>
                </Chip.Group>
              </Stack>

              {/* Status */}
              <Stack gap={4}>
                <Text size="xs" c="dimmed" fw={500}>Status</Text>
                <Chip.Group
                  multiple={false}
                  value={config.statusFilter}
                  onChange={(v) => v && setConfig(c => ({ ...c, statusFilter: v as StatusFilter }))}
                >
                  <Group gap={4} wrap="wrap">
                    {(['all', 'done', 'wip', 'backlog', 'review', 'archived'] as StatusFilter[]).map(f => (
                      <Chip key={f} value={f} size="xs"
                            variant={config.statusFilter === f ? 'filled' : 'light'}
                            color={f === 'done' ? 'green' : f === 'wip' ? 'blue' : f === 'review' ? 'yellow' : f === 'archived' ? 'gray' : 'grape'}>
                        {statusFilterLabel(f)}
                      </Chip>
                    ))}
                  </Group>
                </Chip.Group>
              </Stack>

              {/* Sort */}
              <Stack gap={4}>
                <Text size="xs" c="dimmed" fw={500}>Sort</Text>
                <Chip.Group
                  multiple={false}
                  value={config.sort}
                  onChange={(v) => v && setConfig(c => ({ ...c, sort: v as SortKey }))}
                >
                  <Group gap={4} wrap="wrap">
                    {(Object.keys(SORT_LABELS) as SortKey[]).map(s => (
                      <Chip key={s} value={s} size="xs"
                            variant={config.sort === s ? 'filled' : 'light'}
                            color="cyan">
                        {SORT_LABELS[s]}
                      </Chip>
                    ))}
                  </Group>
                </Chip.Group>
              </Stack>

              {/* Properties — EVERY defined/observed property (excluding meta fields) */}
              {allProperties
                .filter(p => p.id !== 'status') // legacy 'status' field is already in the Status section above
                .filter(p => !['title', 'date', 'cover', 'finished'].includes(p.id)) // meta fields, not filterable
                .filter(p => p.options.length > 0) // skip properties with no options to filter on
                .map(p => {
                  const selected: string[] = config.propertyFilter[p.id] || [];
                  const toggle = (optId: string) => {
                    setConfig(c => {
                      const cur = c.propertyFilter[p.id] || [];
                      const next = cur.includes(optId) ? cur.filter(x => x !== optId) : [...cur, optId];
                      const pf = { ...c.propertyFilter };
                      if (next.length === 0) delete pf[p.id]; else pf[p.id] = next;
                      return { ...c, propertyFilter: pf };
                    });
                  };
                  return (
                    <Stack key={p.id} gap={4}>
                      <Text size="xs" c="dimmed" fw={500}>
                        {p.name || p.id}
                        {p.source === 'observed' && (
                          <Text component="span" size="9px" c="dimmed" ml={4}>(observed)</Text>
                        )}
                      </Text>
                      <Group gap={4} wrap="wrap">
                        {p.options.length === 0 && (
                          <Text size="xs" c="dimmed" italic>No options yet</Text>
                        )}
                        {p.options.map(o => (
                          <Chip
                            key={o.id}
                            size="xs"
                            variant={selected.includes(o.id) ? 'filled' : 'light'}
                            color={o.color || 'blue'}
                            onClick={() => toggle(o.id)}
                          >
                            {o.name}
                          </Chip>
                        ))}
                      </Group>
                    </Stack>
                  );
                })}

              {/* Display options */}
              <Stack gap={4}>
                <Text size="xs" c="dimmed" fw={500}>Display</Text>
                <Group gap="xs">
                  <Chip.Group
                    multiple={false}
                    value={config.weekStart}
                    onChange={(v) => v && setConfig(c => ({ ...c, weekStart: v as WeekStart }))}
                  >
                    <Group gap={4} wrap="wrap">
                      <Chip value="sunday" size="xs" variant={config.weekStart === 'sunday' ? 'filled' : 'light'}>Sun start</Chip>
                      <Chip value="monday" size="xs" variant={config.weekStart === 'monday' ? 'filled' : 'light'}>Mon start</Chip>
                    </Group>
                  </Chip.Group>
                  <Chip
                    size="xs"
                    variant={config.hideWeekends ? 'filled' : 'light'}
                    color={config.hideWeekends ? 'red' : 'gray'}
                    onClick={() => setConfig(c => ({ ...c, hideWeekends: !c.hideWeekends }))}
                  >
                    {config.hideWeekends ? 'Hide weekends ✓' : 'Hide weekends'}
                  </Chip>
                </Group>
              </Stack>
            </Stack>
          </Popover.Dropdown>
        </Popover>

        {/* Sort quick indicator (only shows if non-default) */}
        {config.sort !== 'date-asc' && (
          <Badge size="xs" variant="light" color="cyan">
            {SORT_LABELS[config.sort]}
          </Badge>
        )}

        <ActionIcon variant="subtle" color="gray" onClick={() => setHelpOpen(true)} title="Keyboard shortcuts (?)">
          <IconKeyboard size={16} />
        </ActionIcon>
      </Group>

      {/* Active filter chips — dismissable */}
      {activeFilterCount > 0 && (
        <Group gap="xs" align="center" wrap="wrap" mb="xs">
          {config.quickFilter !== 'all' && (
            <Badge size="sm" variant="light" color="red" rightSection={
              <ActionIcon size="xs" variant="transparent" onClick={() => setConfig(c => ({ ...c, quickFilter: 'all' }))}>
                <IconX size={10} />
              </ActionIcon>
            }>
              Where: {quickFilterLabel(config.quickFilter)}
            </Badge>
          )}
          {config.statusFilter !== 'all' && (
            <Badge size="sm" variant="light" color="green" rightSection={
              <ActionIcon size="xs" variant="transparent" onClick={() => setConfig(c => ({ ...c, statusFilter: 'all' }))}>
                <IconX size={10} />
              </ActionIcon>
            }>
              Status: {statusFilterLabel(config.statusFilter)}
            </Badge>
          )}
          {config.zoom !== SMART_DEFAULT_ZOOM[config.timeField] && (
            <Badge size="sm" variant="light" color="teal" rightSection={
              <ActionIcon size="xs" variant="transparent" onClick={() => { setConfig(c => ({ ...c, zoom: SMART_DEFAULT_ZOOM[c.timeField] })); try { localStorage.removeItem(CALENDAR_ZOOM_OVERRIDE_KEY(database.id, config.timeField)); } catch {} }}>
                <IconX size={10} />
              </ActionIcon>
            }>
              Zoom: {ZOOM_LABELS[config.zoom]}
            </Badge>
          )}
          {config.hideWeekends && (
            <Badge size="sm" variant="light" color="red" rightSection={
              <ActionIcon size="xs" variant="transparent" onClick={() => setConfig(c => ({ ...c, hideWeekends: false }))}>
                <IconX size={10} />
              </ActionIcon>
            }>
              Hide weekends
            </Badge>
          )}
          {/* Property filter chips — one chip per property, with multiple options */}
          {Object.entries(config.propertyFilter || {}).map(([propId, optionIds]) => {
            if (!optionIds || optionIds.length === 0) return null;
            const propDef = allProperties.find(p => p.id === propId);
            if (!propDef) return null;
            const allOptions: Array<{ id: string; name: string }> = propDef.options || [];
            const selectedNames = optionIds.map(id => allOptions.find(o => o.id === id)?.name || id);
            return (
              <Badge key={propId} size="sm" variant="light" color="indigo" rightSection={
                <ActionIcon size="xs" variant="transparent" onClick={() => setConfig(c => {
                  const pf = { ...c.propertyFilter };
                  delete pf[propId];
                  return { ...c, propertyFilter: pf };
                })}>
                  <IconX size={10} />
                </ActionIcon>
              }>
                {propDef.name || propId}: {selectedNames.join(', ')}
              </Badge>
            );
          })}
        </Group>
      )}

      <Modal opened={helpOpen} onClose={() => setHelpOpen(false)} title="Keyboard shortcuts" size="sm">
        <Stack gap="xs">
          <ShortcutRow keys={['T']} desc="Jump to today" />
          <ShortcutRow keys={['←']} desc="Previous zoom unit" />
          <ShortcutRow keys={['→']} desc="Next zoom unit" />
          <ShortcutRow keys={['1']} desc="Zoom: Day" />
          <ShortcutRow keys={['2']} desc="Zoom: 3 days" />
          <ShortcutRow keys={['3']} desc="Zoom: Week" />
          <ShortcutRow keys={['4']} desc="Zoom: Month" />
          <ShortcutRow keys={['5']} desc="Zoom: Quarter" />
          <ShortcutRow keys={['6']} desc="Zoom: Year" />
          <ShortcutRow keys={['M']} desc="Layout: Month" />
          <ShortcutRow keys={['A']} desc="Layout: Agenda" />
          <ShortcutRow keys={['C']} desc="Layout: Columns" />
          <ShortcutRow keys={['\u2318', 'K']} desc="Quick-jump date picker" />
          <ShortcutRow keys={['?']} desc="Show this help" />
          <ShortcutRow keys={['Esc']} desc="Close any dialog" />
        </Stack>
      </Modal>

      {/* Body */}
      {layout === 'month' && (
        <MonthLayout cursor={cursor} itemsByDate={itemsByDate} weekStart={config.weekStart} hideWeekends={config.hideWeekends} onItemSelect={onItemSelect} onItemUpdate={onItemUpdate} datePropertyId={datePropertyId} allItems={items} onPrev={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1))} onNext={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1))} onToday={() => setCursor(new Date())} onJumpToMonth={(y, m) => setCursor(new Date(y, m, 1))} />
      )}
      {layout === 'agenda' && (
        <AgendaLayout cursor={cursor} direction={direction} itemsByDate={itemsByDate} items={items} timeField={timeField} datePropertyId={datePropertyId} onItemSelect={onItemSelect} onItemUpdate={onItemUpdate} />
      )}
      {layout === 'columns' && (
        <ColumnsLayout direction={direction} itemsByDate={itemsByDate} zoom={zoom} onItemSelect={onItemSelect} onAddItem={onItemCreate} onItemUpdate={onItemUpdate} datePropertyId={datePropertyId} allItems={items} />
      )}
    </Box>
  );
};

// ─── Month layout (7×6 grid) ─────────────────────────────────────────────
const MonthLayout: React.FC<{
  cursor: Date;
  itemsByDate: Map<string, DatabaseItem[]>;
  weekStart: 'sunday' | 'monday';
  hideWeekends: boolean;
  onItemSelect?: (item: DatabaseItem) => void;
  onItemCreate?: (date: Date) => void;
  onItemUpdate?: (item: DatabaseItem) => void;
  datePropertyId?: string;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onJumpToMonth: (year: number, month: number) => void;
  allItems: DatabaseItem[];
}> = ({ cursor, itemsByDate, weekStart, hideWeekends, onItemSelect, onItemCreate, onItemUpdate, datePropertyId, onPrev, onNext, onToday, onJumpToMonth, allItems }) => {
  const monthName = cursor.toLocaleString('default', { month: 'long', year: 'numeric' });
  // Build a 6-row × 7-col grid of Date objects (including other-month spillover)
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startDay = firstOfMonth.getDay(); // 0=Sun
  // If Monday-start, shift so that column 0 is Monday (we'll handle weekdays list below)
  const offset = weekStart === 'monday' ? (startDay === 0 ? 6 : startDay - 1) : startDay;
  const startDate = new Date(year, month, 1 - offset);
  let cells: Date[] = [];
  for (let i = 0; i < 42; i++) cells.push(new Date(startDate.getTime() + i * 86400000));
  // If hideWeekends, filter out Sat/Sun cells (and re-shape the grid)
  if (hideWeekends) {
    cells = cells.filter(d => d.getDay() !== 0 && d.getDay() !== 6);
  }
  // Group cells into weeks
  const daysPerRow = hideWeekends ? 5 : 7;
  const weeks: Date[][] = [];
  for (let i = 0; i < 6; i++) weeks.push(cells.slice(i * daysPerRow, i * daysPerRow + daysPerRow));
  // Day-of-week labels
  const dayLabels = weekStart === 'monday'
    ? (hideWeekends ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'])
    : (hideWeekends ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']);

  return (
    <Paper p="md" radius="md" withBorder>
      <Group justify="space-between" align="center" mb="md">
        <ActionIcon onClick={onPrev} variant="subtle" aria-label="Previous month"><IconChevronLeft size={16} /></ActionIcon>
        <Text fw={600} size="md">{monthName}</Text>
        <ActionIcon onClick={onNext} variant="subtle" aria-label="Next month"><IconChevronRight size={16} /></ActionIcon>
      </Group>
      <SimpleGrid cols={daysPerRow + 1} mb="sm">
        <Text size="xs" fw={500} c="dimmed" ta="center">Wk</Text>
        {dayLabels.map(d => (
          <Text key={d} size="xs" fw={500} c="dimmed" ta="center">{d}</Text>
        ))}
      </SimpleGrid>
      <Stack gap={4}>
        {weeks.map((week, wi) => {
          const weekDate = week[0];
          const wn = isoWeek(weekDate);
          return (
            <SimpleGrid key={wi} cols={daysPerRow + 1} spacing={4}>
              {/* Week number cell (left margin) */}
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Badge size="xs" variant="light" color="gray" title={`ISO week ${wn}`}>
                  W{wn}
                </Badge>
              </Box>
              {week.map((d, di) => {
                const isOther = d.getMonth() !== month;
                const today = isSameDay(d, new Date());
                const dayItems = itemsByDate.get(fmtDay(d)) || [];
                const dropHandlers = makeDayDropHandlers(d, allItems, onItemUpdate, datePropertyId);
                return (
                  <Paper
                    key={di}
                    p={4}
                    radius="sm"
                    withBorder
                    {...dropHandlers}
                    onClick={() => {
                      if (isOther) {
                        onJumpToMonth(d.getFullYear(), d.getMonth());
                      } else {
                        onItemCreate?.(d);
                      }
                    }}
                    style={{
                      minHeight: 80,
                      backgroundColor: today ? 'var(--mantine-color-blue-light)' : isOther ? 'var(--fc-bg-secondary, #fafafa)' : 'var(--fc-bg-primary)',
                      borderColor: today ? 'var(--mantine-color-blue-filled)' : 'var(--fc-border-primary)',
                      color: isOther ? 'var(--fc-text-hint, #999)' : 'var(--fc-text-primary)',
                      cursor: 'pointer',
                      overflow: 'auto',
                      position: 'relative',
                    }}
                  >
                    <Text size="xs" fw={today ? 700 : 500} mb={2}>
                      {d.getDate()}
                    </Text>
                    <Stack gap={2}>
                      {dayItems.slice(0, 2).map(item => {
                        const dragHandlers = makeItemDragHandlers(item, onItemUpdate, datePropertyId);
                        return (
                          <Text
                            key={item.id}
                            size="xs"
                            lineClamp={1}
                            draggable={dragHandlers.draggable}
                            onDragStart={dragHandlers.onDragStart}
                            onDragEnd={dragHandlers.onDragEnd}
                            onClick={(e) => { e.stopPropagation(); onItemSelect?.(item); }}
                            style={{
                              padding: '2px 4px',
                              borderRadius: 3,
                              backgroundColor: 'var(--fc-bg-primary)',
                              borderLeft: '2px solid var(--mantine-color-blue-filled)',
                              cursor: 'grab',
                            }}
                          >
                            {item.title}
                          </Text>
                        );
                      })}
                      {dayItems.length > 2 && <Text size="xs" c="dimmed">+{dayItems.length - 2} more</Text>}
                    </Stack>
                  </Paper>
                );
              })}
            </SimpleGrid>
          );
        })}
      </Stack>
      <Group justify="center" mt="md">
        <Badge variant="light" onClick={onToday} style={{ cursor: 'pointer' }}>Jump to today</Badge>
      </Group>
    </Paper>
  );
};

// ─── Agenda layout (vertical list, day-grouped) ──────────────────────────
const AgendaLayout: React.FC<{
  cursor: Date;
  direction: 'forward' | 'backward';
  itemsByDate: Map<string, DatabaseItem[]>;
  items: DatabaseItem[];
  timeField: TimeField;
  datePropertyId?: string;
  onItemSelect?: (item: DatabaseItem) => void;
  onItemUpdate?: (item: DatabaseItem) => void;
}> = ({ cursor, direction, itemsByDate, items, timeField, datePropertyId, onItemSelect, onItemUpdate }) => {
  const today = startOfDay(new Date());
  // Build sorted list of days that have items
  const days = useMemo(() => {
    const keys = [...itemsByDate.keys()].sort();
    return keys.map(k => parseDay(k));
  }, [itemsByDate]);
  // Split into past (before today) and future (today onwards)
  const past = days.filter(d => d < today).reverse();
  const upcoming = days.filter(d => d >= today);
  // For backward (history) direction, we mostly care about past, so show past first
  // For forward (planner) direction, show upcoming first
  const ordered = direction === 'backward' ? [...past, ...upcoming] : [...upcoming, ...past];

  // Accordion state — default-open the day closest to today, then the
  // day with the most items. Other sections collapsed.
  // Keyed by yyyy-mm-dd string.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    const c = new Set<string>();
    if (ordered.length === 0) return c;
    // Find a sensible default-open day
    const todayKey = fmtDay(today);
    const hasToday = ordered.some(d => fmtDay(d) === todayKey);
    let openDay: Date;
    if (hasToday) {
      openDay = ordered.find(d => fmtDay(d) === todayKey)!;
    } else {
      // Pick the day closest to today
      openDay = ordered.reduce((closest, d) => {
        const dc = Math.abs(d.getTime() - today.getTime());
        const cc = Math.abs(closest.getTime() - today.getTime());
        return dc < cc ? d : closest;
      });
    }
    const openKey = fmtDay(openDay);
    for (const d of ordered) {
      if (fmtDay(d) !== openKey) c.add(fmtDay(d));
    }
    return c;
  });

  // Re-seed collapsed set when the day list changes — preserve the
  // user's explicit open/close choices, but always keep at least one
  // section open (the closest to today, or today itself if present).
  useEffect(() => {
    setCollapsed(prev => {
      if (ordered.length === 0) return prev;
      const current = new Set(ordered.map(d => fmtDay(d)));
      // Drop keys for days that no longer exist
      const next = new Set<string>();
      for (const k of prev) if (current.has(k)) next.add(k);
      // Add any new day that isn't the chosen open day
      const todayKey = fmtDay(today);
      const hasToday = ordered.some(d => fmtDay(d) === todayKey);
      const openKey = hasToday ? todayKey : fmtDay(ordered.reduce((closest, d) => {
        const dc = Math.abs(d.getTime() - today.getTime());
        const cc = Math.abs(closest.getTime() - today.getTime());
        return dc < cc ? d : closest;
      }));
      for (const d of ordered) {
        const k = fmtDay(d);
        if (k !== openKey) next.add(k);
      }
      return next;
    });
  }, [ordered.length, today.getTime()]);

  const toggle = (k: string) => setCollapsed(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const allCollapsed = ordered.length > 0 && ordered.every(d => !isSameDay(d, today) && collapsed.has(fmtDay(d)));
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => {
    const next = new Set<string>();
    for (const d of ordered) next.add(fmtDay(d));
    setCollapsed(next);
  };

  return (
    <Paper p="md" radius="md" withBorder>
      <Group justify="space-between" mb="sm">
        <Text size="xs" c="dimmed" tt="uppercase" fw={500}>Sections</Text>
        <Group gap="xs">
          <Button size="xs" variant="light" color="blue" onClick={allCollapsed ? expandAll : collapseAll}>
            {allCollapsed ? 'Expand all' : 'Collapse all'}
          </Button>
        </Group>
      </Group>
      <Stack gap="xs">
        {ordered.length === 0 ? (
          <Text c="dimmed" ta="center" py="lg">
            {config.quickFilter !== 'all' || config.statusFilter !== 'all'
              ? 'No items match these filters'
              : 'No items match the current time field'}
          </Text>
        ) : ordered.map(d => {
          const k = fmtDay(d);
          const isT = isSameDay(d, today);
          const isOpen = isT || !collapsed.has(k);
          const items = itemsByDate.get(k) || [];
          const dropHandlers = makeDayDropHandlers(d, items, onItemUpdate, datePropertyId);
          return (
            <Box
              key={k}
              {...dropHandlers}
              style={{
                border: '1px solid var(--fc-border-primary)',
                borderRadius: 6,
                overflow: 'hidden',
                backgroundColor: isT ? 'var(--fc-bg-blue-subtle, rgba(59,130,246,0.04))' : 'transparent',
              }}
            >
              <Group
                gap="sm"
                align="center"
                p="xs"
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => toggle(k)}
              >
                <ActionIcon size="sm" variant="subtle" color="gray" onClick={(e) => { e.stopPropagation(); toggle(k); }}>
                  {isOpen ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                </ActionIcon>
                <Text fw={isT ? 700 : 500} size="sm" c="var(--fc-text-primary)">
                  {d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                </Text>
                {isT && <Badge size="xs" variant="light" color="gray">Today</Badge>}
                <Badge size="xs" variant="light" color="gray">{items.length} item{items.length === 1 ? '' : 's'}</Badge>
              </Group>
              {isOpen && (
                <Stack gap={4} p="xs" pt={0} ml="md" pl="sm" style={{ borderLeft: '2px solid var(--fc-border-primary)' }}>
                  {items.map(item => {
                    const dragHandlers = makeItemDragHandlers(item, onItemUpdate, datePropertyId);
                    return (
                      <Box
                        key={item.id}
                        p="xs"
                        draggable={dragHandlers.draggable}
                        onDragStart={dragHandlers.onDragStart}
                        onDragEnd={dragHandlers.onDragEnd}
                        style={{
                          backgroundColor: 'var(--fc-bg-secondary, #fafafa)',
                          borderRadius: 4,
                          cursor: 'grab',
                        }}
                        onClick={() => onItemSelect?.(item)}
                      >
                        <Text size="sm" fw={500} lineClamp={1}>{item.title}</Text>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

// ─── Columns layout (horizontal kanban by date) ─────────────────────────
// Adapts to all 6 zoom levels:
//   day:      1 day per column (200px)
//   3-day:    3 days per column (320px, internal day dividers)
//   week:     1 week per column (7 day-rows, 360px)
//   month:    1 month per column (mini 7x6 grid, 380px)
//   quarter:  1 quarter per column (3 month-mini-grids, 600px)
//   year:     1 year per column (12 month bands, 900px)
const ColumnsLayout: React.FC<{
  direction: 'forward' | 'backward';
  itemsByDate: Map<string, DatabaseItem[]>;
  zoom: ZoomLevel;
  onItemSelect?: (item: DatabaseItem) => void;
  onAddItem?: (item: DatabaseItem) => void;
  onItemUpdate?: (item: DatabaseItem) => void;
  datePropertyId?: string;
  allItems: DatabaseItem[];
}> = ({ direction, itemsByDate, zoom, onItemSelect, onItemUpdate, datePropertyId, allItems }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const today = startOfDay(new Date());
  // Number of columns to render — decreases as zoom widens
  const STEPS = zoom === 'day' ? 14 : zoom === '3-day' ? 10 : zoom === 'week' ? 8 : zoom === 'month' ? 6 : zoom === 'quarter' ? 4 : 4;
  const stepDays = zoomStepDaysLocal(zoom);

  // Build the list of windows (one window = one column)
  const windows: Date[] = [];
  for (let i = 0; i < STEPS; i++) {
    const offset = direction === 'backward' ? -i * stepDays : i * stepDays;
    windows.push(new Date(today.getTime() + offset * 86400000));
  }

  // After mount, scroll the today column into view (left of viewport)
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  }, [direction, zoom]);

  return (
    <div
      ref={scrollRef}
      className="fc-calendar-columns-scroll"
      style={{
        display: 'flex',
        gap: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        scrollSnapType: 'x proximity',
        paddingBottom: 8,
      }}
    >
      {windows.map((w, idx) => {
        // Column widths chosen so 2 columns fit comfortably on a 390px mobile
        // viewport (with sidebar closed) and 3-5 fit on desktop.
        // day: 1 day per col  | 3-day: 3 days per col | week: 7 days per col
        // month: 1 month per col (7-col day grid) | quarter: 3 months stacked
        // year: 4 quarters stacked
        const colWidth = zoom === 'day' ? 160 : zoom === '3-day' ? 180 : zoom === 'week' ? 200 : zoom === 'month' ? 280 : zoom === 'quarter' ? 400 : 560;
        // First column is anchored on today
        const isFirst = idx === 0;
        return (
          <ColumnBlock
            key={w.getTime()}
            anchor={w}
            isFirst={isFirst}
            zoom={zoom}
            itemsByDate={itemsByDate}
            colWidth={colWidth}
            onItemSelect={onItemSelect}
            onItemUpdate={onItemUpdate}
            datePropertyId={datePropertyId}
            allItems={allItems}
          />
        );
      })}
    </div>
  );
};

const ColumnBlock: React.FC<{
  anchor: Date;
  isFirst: boolean;
  zoom: ZoomLevel;
  itemsByDate: Map<string, DatabaseItem[]>;
  colWidth: number;
  onItemSelect?: (item: DatabaseItem) => void;
  onItemUpdate?: (item: DatabaseItem) => void;
  datePropertyId?: string;
  allItems: DatabaseItem[];
}> = ({ anchor, isFirst, zoom, itemsByDate, colWidth, onItemSelect, onItemUpdate, datePropertyId, allItems }) => {
  const today = startOfDay(new Date());
  // Build the days in this column
  const days: Date[] = [];
  const step = zoomStepDaysLocal(zoom);
  for (let i = 0; i < step; i++) {
    days.push(new Date(anchor.getTime() + i * 86400000));
  }
  // Header label depends on zoom
  const headerLabel = (() => {
    if (zoom === 'day') return days[0].toLocaleString('default', { weekday: 'short', month: 'short', day: 'numeric' });
    if (zoom === '3-day') {
      const start = days[0];
      const end = days[days.length - 1];
      return `${start.toLocaleString('default', { month: 'short', day: 'numeric' })} – ${end.toLocaleString('default', { month: 'short', day: 'numeric' })}`;
    }
    if (zoom === 'week') {
      const end = days[days.length - 1];
      return `Week of ${days[0].toLocaleString('default', { month: 'short', day: 'numeric' })} – ${end.getDate()}`;
    }
    if (zoom === 'month') return anchor.toLocaleString('default', { month: 'long', year: 'numeric' });
    if (zoom === 'quarter') {
      const q = Math.floor(anchor.getMonth() / 3) + 1;
      return `Q${q} ${anchor.getFullYear()}`;
    }
    return anchor.getFullYear().toString();
  })();
  // Year-month group label (large band above)
  const monthBand = anchor.toLocaleString('default', { month: 'long', year: 'numeric' });
  // At day/3-day/week zoom, this column corresponds to a single day (or
  // range). Use the anchor as the drop target so the user can drop
  // items onto a specific day in the columns view. For month+ zoom,
  // we drop onto the first of the month (precision: month).
  const dropTargetDate = zoom === 'day' || zoom === '3-day' || zoom === 'week' ? anchor : new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const dropHandlers = makeDayDropHandlers(dropTargetDate, allItems, onItemUpdate, datePropertyId);

  return (
    <Box
      {...dropHandlers}
      style={{
        width: colWidth,
        minWidth: colWidth,
        maxWidth: colWidth,
        flexShrink: 0,
        borderRight: '1px solid var(--fc-border-tertiary, #f0f0f0)',
        backgroundColor: 'var(--fc-bg-primary)',
        display: 'flex',
        flexDirection: 'column',
        scrollSnapAlign: 'start',
        minHeight: 400,
        maxHeight: 520,
      }}
    >
      {/* Header band — month + zoom-specific label. Clean, no blue fill. */}
      <Box
        p="xs"
        style={{
          borderBottom: '1px solid var(--fc-border-tertiary, #f0f0f0)',
          backgroundColor: 'var(--fc-bg-secondary, #fafafa)',
        }}
      >
        <Text size="xs" c="dimmed" tt="uppercase" fw={500} mb={2}>{monthBand}</Text>
        <Text size="sm" fw={500} c="var(--fc-text-primary)">
          {headerLabel}
        </Text>
        {isFirst && (
          <Text size="xs" c="dimmed" fw={600} mt={2} style={{ letterSpacing: '0.04em' }}>
            TODAY
          </Text>
        )}
      </Box>
      {/* Body — for day zoom: just one day. For 3-day: 3 rows. For week: 7 day-rows. For month/quarter/year: cards. */}
      <Stack gap={0} p={0} style={{ flex: 1, overflowY: 'auto' }}>
        {zoom === 'day' && (
          <DayColumnBody day={days[0]} items={itemsByDate.get(fmtDay(days[0])) || []} isToday={isSameDay(days[0], today)} onItemSelect={onItemSelect} onItemUpdate={onItemUpdate} datePropertyId={datePropertyId} />
        )}
        {zoom === '3-day' && (
          days.map(d => <DayColumnBody key={fmtDay(d)} day={d} items={itemsByDate.get(fmtDay(d)) || []} isToday={isSameDay(d, today)} onItemSelect={onItemSelect} onItemUpdate={onItemUpdate} datePropertyId={datePropertyId} compact />)
        )}
        {zoom === 'week' && (
          days.map(d => <DayColumnBody key={fmtDay(d)} day={d} items={itemsByDate.get(fmtDay(d)) || []} isToday={isSameDay(d, today)} onItemSelect={onItemSelect} onItemUpdate={onItemUpdate} datePropertyId={datePropertyId} compact />)
        )}
        {zoom === 'month' && (
          <MonthColumnBody monthAnchor={days[0]} itemsByDate={itemsByDate} today={today} onItemSelect={onItemSelect} />
        )}
        {zoom === 'quarter' && (
          <QuarterColumnBody quarterAnchor={days[0]} itemsByDate={itemsByDate} today={today} onItemSelect={onItemSelect} />
        )}
        {zoom === 'year' && (
          <YearColumnBody yearAnchor={days[0]} itemsByDate={itemsByDate} today={today} onItemSelect={onItemSelect} />
        )}
      </Stack>
    </Box>
  );
};

const DayColumnBody: React.FC<{
  day: Date;
  items: DatabaseItem[];
  isToday: boolean;
  compact?: boolean;
  onItemSelect?: (item: DatabaseItem) => void;
  onItemUpdate?: (item: DatabaseItem) => void;
  datePropertyId?: string;
}> = ({ day, items, isToday, compact, onItemSelect, onItemUpdate, datePropertyId }) => {
  const dropHandlers = makeDayDropHandlers(day, items, onItemUpdate, datePropertyId);
  return (
    <Box {...dropHandlers} style={{ borderBottom: '1px solid var(--fc-border-tertiary, #f0f0f0)' }}>
    <Group gap={4} p="xs" align="center"
           style={{ backgroundColor: isToday ? 'var(--fc-bg-secondary, #fafafa)' : 'transparent' }}>
      <Text size="xs" c={isToday ? 'var(--fc-text-primary)' : 'dimmed'} tt="uppercase" fw={isToday ? 600 : 500}>
        {day.toLocaleString('default', { weekday: 'short' })}
      </Text>
      <Text size="sm" fw={isToday ? 700 : 500} c="var(--fc-text-primary)">
        {day.getDate()}
      </Text>
      <Badge size="xs" variant="light" color="gray">{items.length}</Badge>
    </Group>
    {!compact && items.length === 0 && (
      <Text size="xs" c="dimmed" ta="center" py="md">—</Text>
    )}
    {items.map(item => {
      const dragHandlers = makeItemDragHandlers(item, onItemUpdate, datePropertyId);
      return (
        <Paper
          key={item.id}
          p="xs"
          mx="xs"
          mb="xs"
          radius="sm"
          withBorder
          draggable={dragHandlers.draggable}
          onDragStart={dragHandlers.onDragStart}
          onDragEnd={dragHandlers.onDragEnd}
          style={{ backgroundColor: 'var(--fc-bg-primary)', cursor: 'grab' }}
          onClick={() => onItemSelect?.(item)}
        >
          <Text size="xs" fw={500} lineClamp={1}>{item.title}</Text>
        </Paper>
      );
    })}
  </Box>
  );
};

const MonthColumnBody: React.FC<{
  monthAnchor: Date;
  itemsByDate: Map<string, DatabaseItem[]>;
  today: Date;
  onItemSelect?: (item: DatabaseItem) => void;
}> = ({ monthAnchor, itemsByDate, today, onItemSelect }) => {
  // Build 6-row x 7-col grid for the month
  const year = monthAnchor.getFullYear();
  const month = monthAnchor.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startDay = firstOfMonth.getDay(); // 0=Sun
  const startDate = new Date(year, month, 1 - startDay);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) cells.push(new Date(startDate.getTime() + i * 86400000));
  return (
    <Box p="xs">
      <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map(d => {
          const isOther = d.getMonth() !== month;
          const isT = isSameDay(d, today);
          const items = itemsByDate.get(fmtDay(d)) || [];
          return (
            <Box key={fmtDay(d)} p={3}
                 style={{ minHeight: 36, backgroundColor: isOther ? 'var(--fc-bg-secondary, #fafafa)' : 'var(--fc-bg-primary)', borderRadius: 3, border: isT ? '1.5px solid var(--fc-text-primary)' : '1px solid var(--fc-border-tertiary, #f0f0f0)' }}>
              <Text size="9px" c={isOther ? 'dimmed' : 'var(--fc-text-primary)'} fw={isT ? 700 : 400}>
                {d.getDate()}
              </Text>
              {items.length > 0 && (
                <Text size="9px" c="var(--fc-text-secondary)" fw={500} lineClamp={1} style={{ marginTop: 1 }}>
                  {items.length} item{items.length === 1 ? '' : 's'}
                </Text>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const QuarterColumnBody: React.FC<{
  quarterAnchor: Date;
  itemsByDate: Map<string, DatabaseItem[]>;
  today: Date;
  onItemSelect?: (item: DatabaseItem) => void;
}> = ({ quarterAnchor, itemsByDate, today, onItemSelect }) => {
  const startMonth = Math.floor(quarterAnchor.getMonth() / 3) * 3;
  return (
    <Stack gap="xs" p="xs">
      {[0, 1, 2].map(i => {
        const m = new Date(quarterAnchor.getFullYear(), startMonth + i, 1);
        return (
          <Box key={i}>
            <Text size="xs" fw={500} c="dimmed" tt="uppercase" mb={2}>
              {m.toLocaleString('default', { month: 'long' })}
            </Text>
            <MonthColumnBody monthAnchor={m} itemsByDate={itemsByDate} today={today} onItemSelect={onItemSelect} />
          </Box>
        );
      })}
    </Stack>
  );
};

const YearColumnBody: React.FC<{
  yearAnchor: Date;
  itemsByDate: Map<string, DatabaseItem[]>;
  today: Date;
  onItemSelect?: (item: DatabaseItem) => void;
}> = ({ yearAnchor, itemsByDate, today, onItemSelect }) => {
  return (
    <Stack gap="xs" p="xs">
      {[0, 1, 2, 3].map(q => {
        const qAnchor = new Date(yearAnchor.getFullYear(), q * 3, 1);
        return (
          <Box key={q}>
            <Text size="xs" fw={500} c="dimmed" tt="uppercase" mb={2}>
              Q{q + 1} {yearAnchor.getFullYear()}
            </Text>
            <QuarterColumnBody quarterAnchor={qAnchor} itemsByDate={itemsByDate} today={today} onItemSelect={onItemSelect} />
          </Box>
        );
      })}
    </Stack>
  );
};

function zoomStepDaysLocal(zoom: ZoomLevel): number {
  switch (zoom) {
    case 'day':     return 1;
    case '3-day':  return 3;
    case 'week':   return 7;
    case 'month':  return 30;
    case 'quarter': return 91;
    case 'year':   return 365;
  }
}

const ShortcutRow: React.FC<{ keys: string[]; desc: string }> = ({ keys, desc }) => (
  <Group justify="space-between" align="center">
    <Text size="sm">{desc}</Text>
    <Group gap={4}>
      {keys.map((k, i) => <Kbd key={i} style={{ fontSize: 11 }}>{k}</Kbd>)}
    </Group>
  </Group>
);

export default CalendarView;
